/* Floor-plan SVG renderer — reads the typed FloorPlan node attached
 * to a Room (via HAS_FLOORPLAN) and renders its widthFt/depthFt and
 * door/window opening polygons. Conforms to the CubiCasa5K vector
 * shape (Zenodo 2613548, MIT, 5K real-estate plans). On ingest
 * (MinerU / ezdxf), real polygons populate these typed attrs; the
 * renderer is identical either way — graph is the contract.
 */

import type { GraphEdge, GraphNode, ProjectGraph } from "../types/ontology";
import { parseOpenings as parseOpeningsShared, type Opening } from "./openings";

interface PlanInput {
  graph: ProjectGraph;
  roomId: string;
  /** Pixel width of the output. The SVG viewBox scales independently. */
  width?: number;
  /** Pixel height of the output. */
  height?: number;
  /** When true, omit SKU icons from the SVG — the React FloorPlanOverlay
   *  renders them as interactive draggable elements instead. Keeps the
   *  2D and 3D views structurally in sync because both read the same
   *  posX_${roomId} / posZ_${roomId} typed overrides. */
  skipSkuIcons?: boolean;
}

/** Geometry the React overlay needs to position interactive SKU markers
 *  in the same viewBox coordinates the SVG string uses. Returned by
 *  getPlanMetrics() so callers don't re-derive it. */
export interface PlanMetrics {
  readonly viewBox: { readonly w: number; readonly h: number };
  /** Room origin (top-left) inside the viewBox, in SVG units. */
  readonly originPx: { readonly x: number; readonly y: number };
  /** Room dimensions in SVG units (one foot = 12 SVG units). */
  readonly roomPx: { readonly w: number; readonly h: number };
  /** Room dimensions in feet. */
  readonly roomFt: { readonly w: number; readonly d: number };
  /** Pixels per foot (also = SVG units per foot, since viewBox scales). */
  readonly pxPerFt: number;
  /** Metres per foot — keeps the overlay's room-relative coordinates
   *  consistent with Scene3D + lib/userPlacements (always metres). */
  readonly ftPerM: number;
}

export function getPlanMetrics(args: PlanInput): PlanMetrics | null {
  const room = args.graph.nodes.find((n) => n.id === args.roomId && n.kind === "Room");
  if (!room) return null;
  const plan = floorPlanFor(args.graph, args.roomId);
  if (!plan) return null;
  const rw = Number(attr<number>(plan, "widthFt") ?? 0);
  const rd = Number(attr<number>(plan, "depthFt") ?? 0);
  if (rw <= 0 || rd <= 0) return null;
  const SCALE = 12;
  const padding = 60;
  const vbW = Math.max(rw, rd) * SCALE + padding * 2;
  const vbH = vbW * 0.66;
  const x0 = (vbW - rw * SCALE) / 2;
  const y0 = (vbH - rd * SCALE) / 2;
  return {
    viewBox: { w: vbW, h: vbH },
    originPx: { x: x0, y: y0 },
    roomPx: { w: rw * SCALE, h: rd * SCALE },
    roomFt: { w: rw, d: rd },
    pxPerFt: SCALE,
    // 1 metre = 3.28084 feet; expose its reciprocal so the overlay can
    // convert metres (the lib/userPlacements canonical) to feet quickly.
    ftPerM: 1 / 0.3048,
  };
}

function attr<T = unknown>(n: GraphNode | undefined, key: string): T | undefined {
  if (!n) return undefined;
  const v = n.attrs[key];
  return v as T | undefined;
}

// parseOpenings now lives in lib/openings.ts — shared with Scene3D.
const parseOpenings = (raw: string | undefined): Opening[] => parseOpeningsShared(raw ?? null);

/** FloorPlan attached to a Room — walks the HAS_FLOORPLAN edge. */
function floorPlanFor(graph: ProjectGraph, roomId: string): GraphNode | undefined {
  const edge = graph.edges.find(
    (edg: GraphEdge) => edg.kind === "HAS_FLOORPLAN" && edg.source === roomId,
  );
  if (!edge) return undefined;
  return graph.nodes.find((n) => n.id === edge.target && n.kind === "FloorPlan");
}

/** Count zones inside this room — derived from "WITHIN" edges where
 *  the target is the room. Used to render zone partition lines. */
function zonesInRoom(graph: ProjectGraph, roomId: string): GraphNode[] {
  const zoneEdges = graph.edges.filter(
    (edg: GraphEdge) => edg.kind === "WITHIN" && edg.target === roomId,
  );
  return zoneEdges
    .map((edg) => graph.nodes.find((n) => n.id === edg.source && n.kind === "Zone"))
    .filter((x): x is GraphNode => Boolean(x));
}

/** Render a CubiCasa5K-style vector floor plan as SVG markup. Reads
 *  the FloorPlan node's typed attrs (widthFt, depthFt, openings) and
 *  the Zone graph. No hardcoded fallbacks — if the room has no
 *  FloorPlan node, returns an empty string and the caller renders an
 *  explicit "no plan attached" UI. */
export function renderFloorPlanSvg({ graph, roomId, width = 640, height = 420, skipSkuIcons = false }: PlanInput): string {
  const room = graph.nodes.find((n) => n.id === roomId && n.kind === "Room");
  if (!room) return "";
  const plan = floorPlanFor(graph, roomId);
  if (!plan) return "";

  const ceilingHt = Number(attr<number>(room, "ceilingHt") ?? 10);
  const rw = Number(attr<number>(plan, "widthFt") ?? 0);
  const rd = Number(attr<number>(plan, "depthFt") ?? 0);
  if (rw <= 0 || rd <= 0) return "";
  const planId     = String(attr<string>(plan, "planId") ?? "");
  const planSource = String(attr<string>(plan, "planSource") ?? "CubiCasa5K");
  const doors   = parseOpenings(attr<string>(plan, "doorOpenings"));
  const windows = parseOpenings(attr<string>(plan, "windowOpenings"));

  // SVG units = inches. viewBox sized to room dimensions in feet,
  // scaled ×12 so each foot is 12 svg-units (one foot per twelve).
  const SCALE = 12;
  const padding = 60;
  const vbW = Math.max(rw, rd) * SCALE + padding * 2;
  const vbH = vbW * 0.66;

  // Room outline — anchored centred in the viewBox.
  const x0 = (vbW - rw * SCALE) / 2;
  const y0 = (vbH - rd * SCALE) / 2;
  const x1 = x0 + rw * SCALE;
  const y1 = y0 + rd * SCALE;

  const zones = zonesInRoom(graph, roomId);

  // SKUs placed in this room via IN_ROOM edges — drawn as labeled icons
  // ON the plan (not listed below). Categorised by label keywords so a
  // sofa renders as a wide horizontal rect, a pendant as an overhead
  // circle, a dining table as a central oval, etc.
  const inRoomSkus = graph.nodes.filter((n) =>
    n.kind === "SKU" && graph.edges.some(
      (e) => e.kind === "IN_ROOM" && e.source === n.id && e.target === roomId,
    ),
  );
  // Door + window bounding rects in viewBox space — passed to the SKU layout
  // function so it can avoid placing furniture inside the door swing or right
  // up against the window glazing. CLAUDE.md §1: no hardcoded assumptions
  // about which walls have doors — read the typed openings.
  const doorZones = doors.map((o) => doorSwingZone(o, x0, y0, rw * SCALE, rd * SCALE));
  // When the React FloorPlanOverlay is mounted (RoomStage), it owns the
  // interactive SKU markers, so the SVG just paints the room shell.
  const skuIcons = skipSkuIcons ? "" : layoutSkuIcons(inRoomSkus, x0, y0, rw * SCALE, rd * SCALE, doorZones);

  // Door & window opening geometry — derived from the typed Opening[]
  // arrays on the FloorPlan node. Each Opening carries its wall +
  // start ratio + width in feet.
  function openingRect(o: Opening): { x: number; y: number; w: number; h: number; vertical: boolean } {
    const wPx = o.width * SCALE;
    if (o.wall === "top" || o.wall === "bottom") {
      const xStart = x0 + (rw * SCALE) * o.start;
      const y = o.wall === "top" ? y0 - 1.5 : y1 - 1.5;
      return { x: xStart, y, w: wPx, h: 3, vertical: false };
    }
    const yStart = y0 + (rd * SCALE) * o.start;
    const x = o.wall === "left" ? x0 - 1.5 : x1 - 1.5;
    return { x, y: yStart, w: 3, h: wPx, vertical: true };
  }
  const doorRects = doors.map(openingRect);
  const windowRects = windows.map(openingRect);

  // Zone partition lines — one vertical line per zone, evenly spaced.
  const zoneLines = zones.map((_z, i) => {
    const t = (i + 1) / (zones.length + 1);
    const x = x0 + rw * SCALE * t;
    return `<line x1="${x}" y1="${y0}" x2="${x}" y2="${y1}" stroke="#94908a" stroke-width="0.8" stroke-dasharray="4 3" />`;
  }).join("");

  const zoneLabels = zones.map((z, i) => {
    const t = (i + 0.5) / (zones.length || 1);
    const x = x0 + rw * SCALE * t;
    const y = y0 + rd * SCALE * 0.5;
    return `<text x="${x}" y="${y}" text-anchor="middle" font-family="-apple-system, 'SF Pro Text', Inter, sans-serif" font-size="11" fill="#7a766f" font-style="italic">${escapeXml(z.label)}</text>`;
  }).join("");

  // Scale bar — 5 ft, bottom-left of viewBox.
  const scaleY = vbH - padding * 0.5;
  const scaleStart = padding * 0.8;
  const scaleEnd = scaleStart + 5 * SCALE;

  // Apple-grade visual treatment — hairline walls, soft floor gradient,
  // subtle drop shadow, refined SF-Pro typography. Same typed contract;
  // only the SVG paint changes.
  const sfFont = "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', Inter, system-ui, sans-serif";
  const sfMono = "ui-monospace, 'SF Mono', SFMono-Regular, Menlo, monospace";

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${vbW} ${vbH}" width="${width}" height="${height}" role="img" aria-label="Floor plan for ${escapeXml(room.label)}" style="font-family:${sfFont}">
    <defs>
      <pattern id="grid" width="${SCALE}" height="${SCALE}" patternUnits="userSpaceOnUse">
        <path d="M ${SCALE} 0 L 0 0 0 ${SCALE}" fill="none" stroke="#ECE7DC" stroke-width="0.35"/>
      </pattern>
      <linearGradient id="floor" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stop-color="#FEFDFB"/>
        <stop offset="1" stop-color="#F6F1E7"/>
      </linearGradient>
      <filter id="planShadow" x="-10%" y="-10%" width="120%" height="120%">
        <feGaussianBlur in="SourceAlpha" stdDeviation="4"/>
        <feOffset dy="6" result="off"/>
        <feComponentTransfer in="off" result="soft"><feFuncA type="linear" slope="0.10"/></feComponentTransfer>
        <feMerge><feMergeNode in="soft"/><feMergeNode in="SourceGraphic"/></feMerge>
      </filter>
    </defs>

    <!-- Page background — pure cream -->
    <rect width="${vbW}" height="${vbH}" fill="#FEFDFB"/>

    <!-- Floor slab — soft gradient + drop shadow, rounded corners (Apple-grade) -->
    <g filter="url(#planShadow)">
      <rect x="${x0}" y="${y0}" width="${rw * SCALE}" height="${rd * SCALE}"
            fill="url(#floor)" rx="6" ry="6"/>
    </g>
    <!-- Faint grid INSIDE the slab so it reads as a finished floor, not graph paper -->
    <rect x="${x0}" y="${y0}" width="${rw * SCALE}" height="${rd * SCALE}"
          fill="url(#grid)" rx="6" ry="6" opacity="0.55"/>

    <!-- Walls — hairline ink-950 outer + 0.8 inner stroke for crisp double line -->
    <rect x="${x0 + 0.6}" y="${y0 + 0.6}" width="${rw * SCALE - 1.2}" height="${rd * SCALE - 1.2}"
          fill="none" stroke="#141612" stroke-width="1.6" stroke-linejoin="miter" rx="6" ry="6"/>
    <rect x="${x0 + 2.5}" y="${y0 + 2.5}" width="${rw * SCALE - 5}" height="${rd * SCALE - 5}"
          fill="none" stroke="#141612" stroke-width="0.5" stroke-linejoin="miter" rx="4" ry="4" opacity="0.55"/>

    <!-- Windows — gap + soft glass tint (lighter, more glass-like) -->
    ${windowRects.map((r) => {
      const gap = `<rect x="${r.x}" y="${r.y}" width="${r.w}" height="${r.h}" fill="#FEFDFB" stroke="none"/>`;
      const glaze = r.vertical
        ? `<line x1="${r.x + r.w / 2}" y1="${r.y}" x2="${r.x + r.w / 2}" y2="${r.y + r.h}" stroke="#3D6B9D" stroke-width="1.4" stroke-linecap="round" opacity="0.85"/>`
        : `<line x1="${r.x}" y1="${r.y + r.h / 2}" x2="${r.x + r.w}" y2="${r.y + r.h / 2}" stroke="#3D6B9D" stroke-width="1.4" stroke-linecap="round" opacity="0.85"/>`;
      return gap + glaze;
    }).join("")}

    <!-- Doors — refined arc with rounded leaf indicator -->
    ${doorRects.map((r) => {
      const gap = `<rect x="${r.x}" y="${r.y}" width="${r.w}" height="${r.h}" fill="#FEFDFB" stroke="none"/>`;
      const span = r.vertical ? r.h : r.w;
      const cx = r.x;
      const cy = r.vertical ? r.y : r.y + 1.5;
      const arc = r.vertical
        ? `<path d="M ${cx} ${cy} A ${span} ${span} 0 0 1 ${cx + span} ${cy + span}" fill="none" stroke="#C95C1A" stroke-width="1.2" stroke-linecap="round"/>`
        : `<path d="M ${cx} ${cy + 1.5} A ${span} ${span} 0 0 1 ${cx + span} ${cy + 1.5 - span}" fill="none" stroke="#C95C1A" stroke-width="1.2" stroke-linecap="round"/>`;
      const leaf = r.vertical
        ? `<line x1="${cx}" y1="${cy}" x2="${cx}" y2="${cy + span}" stroke="#C95C1A" stroke-width="1.6" stroke-linecap="round"/>`
        : `<line x1="${cx}" y1="${cy + 1.5}" x2="${cx + span}" y2="${cy + 1.5}" stroke="#C95C1A" stroke-width="1.6" stroke-linecap="round"/>`;
      return gap + leaf + arc;
    }).join("")}

    ${zoneLines}
    ${zoneLabels}
    <!-- SKU placements rendered as labeled iconography ON the plan -->
    ${skuIcons}

    <!-- Dimension annotations — SF Pro tabular, with hairline tick brackets -->
    <g font-family="${sfFont}" fill="#141612">
      <text x="${(x0 + x1) / 2}" y="${y0 - 16}" text-anchor="middle" font-size="11" font-weight="500" letter-spacing="0.2">${rw.toFixed(1)} ft</text>
      <line x1="${x0}" y1="${y0 - 8}" x2="${x1}" y2="${y0 - 8}" stroke="#94908a" stroke-width="0.4"/>
      <line x1="${x0}" y1="${y0 - 11}" x2="${x0}" y2="${y0 - 5}" stroke="#94908a" stroke-width="0.4"/>
      <line x1="${x1}" y1="${y0 - 11}" x2="${x1}" y2="${y0 - 5}" stroke="#94908a" stroke-width="0.4"/>

      <text x="${x0 - 18}" y="${(y0 + y1) / 2}" text-anchor="middle" font-size="11" font-weight="500" letter-spacing="0.2"
            transform="rotate(-90 ${x0 - 18} ${(y0 + y1) / 2})">${rd.toFixed(1)} ft</text>
      <line x1="${x0 - 10}" y1="${y0}" x2="${x0 - 10}" y2="${y1}" stroke="#94908a" stroke-width="0.4"/>
      <line x1="${x0 - 13}" y1="${y0}" x2="${x0 - 7}" y2="${y0}" stroke="#94908a" stroke-width="0.4"/>
      <line x1="${x0 - 13}" y1="${y1}" x2="${x0 - 7}" y2="${y1}" stroke="#94908a" stroke-width="0.4"/>

      <text x="${x1 + 12}" y="${(y0 + y1) / 2}" text-anchor="start" font-size="10" font-weight="400" fill="#7a766f">${ceilingHt.toFixed(1)} ft H</text>
    </g>

    <!-- Scale bar — refined to a single hairline + tab ticks -->
    <g stroke="#141612">
      <line x1="${scaleStart}" y1="${scaleY}" x2="${scaleEnd}" y2="${scaleY}" stroke-width="1" stroke-linecap="round"/>
      <line x1="${scaleStart}" y1="${scaleY - 3}" x2="${scaleStart}" y2="${scaleY + 3}" stroke-width="1"/>
      <line x1="${scaleEnd}" y1="${scaleY - 3}" x2="${scaleEnd}" y2="${scaleY + 3}" stroke-width="1"/>
    </g>
    <text x="${(scaleStart + scaleEnd) / 2}" y="${scaleY - 7}" text-anchor="middle"
          font-family="${sfMono}" font-size="8.5" fill="#7a766f" letter-spacing="0.3">0 — 5 ft</text>

    <!-- Provenance footer — small caps, ink-300, centered -->
    <text x="${vbW / 2}" y="${vbH - 8}" text-anchor="middle"
          font-family="${sfMono}" font-size="7.5" fill="#94908a" letter-spacing="1.2"
          style="text-transform:uppercase">${escapeXml(planId ? `${planSource} · ${planId}` : planSource)}</text>
  </svg>`;
}

/** Bounding rect in viewBox space. */
interface Rect { x: number; y: number; w: number; h: number }

function rectsOverlap(a: Rect, b: Rect): boolean {
  return !(a.x + a.w <= b.x || b.x + b.w <= a.x || a.y + a.h <= b.y || b.y + b.h <= a.y);
}

/** Door swing zone — the gap rectangle plus the 90° arc envelope. Anything
 *  placed inside this zone is "in front of the door" — a real anti-pattern. */
function doorSwingZone(o: Opening, x0: number, y0: number, rwPx: number, rdPx: number): Rect {
  const SCALE = 12;
  const wPx = o.width * SCALE;
  // Swing inset — door arc reaches `width` ft into the room. We add a small
  // clearance buffer (4 px ≈ 4") so SKUs don't kiss the swing edge.
  const buffer = 4;
  const arc = wPx + buffer;
  if (o.wall === "top") {
    const xs = x0 + rwPx * o.start;
    return { x: xs - 2, y: y0 - 2, w: wPx + 4, h: arc };
  }
  if (o.wall === "bottom") {
    const xs = x0 + rwPx * o.start;
    return { x: xs - 2, y: y0 + rdPx - arc, w: wPx + 4, h: arc };
  }
  if (o.wall === "left") {
    const ys = y0 + rdPx * o.start;
    return { x: x0 - 2, y: ys - 2, w: arc, h: wPx + 4 };
  }
  // right
  const ys = y0 + rdPx * o.start;
  return { x: x0 + rwPx - arc, y: ys - 2, w: arc, h: wPx + 4 };
}

/** Place SKU icons inside the room rectangle.
 *  Category comes from ontology-typed SKU.category (codegen.json enum).
 *  Door zones from the typed FloorPlan openings are honored — if a category's
 *  preferred anchor would collide with a door swing, fall through to the next
 *  anchor (center → south → north → east → west → overhead). */
function layoutSkuIcons(
  skus: ReadonlyArray<GraphNode>,
  x0: number, y0: number, rwPx: number, rdPx: number,
  doorZones: ReadonlyArray<Rect>,
): string {
  if (skus.length === 0) return "";
  const cx = x0 + rwPx / 2;
  const cy = y0 + rdPx / 2;

  // Ontology-typed SKU.category dispatch — NO keyword parsing. The category
  // is a typed string field declared in docs/ontology/codegen.json#fields
  // ("SKU.category") with the enum: Sofa | Bed | Dining | Table | Island |
  // Wardrobe | Counter | TV | Study | Bar | Light | Storage | Chair | Rug |
  // Item. Per CLAUDE.md §1, every renderer reads from attrs.category.
  type Anchor = "center" | "south" | "north" | "east" | "west" | "overhead";
  type CategoryCfg = { kind: string; anchor: Anchor; w: number; h: number; shape: "rect" | "circle" | "ellipse" | "strip" };
  const CATEGORY_LAYOUT: Readonly<Record<string, CategoryCfg>> = {
    Sofa:     { kind: "Sofa",     anchor: "south",    w: 80, h: 26, shape: "rect" },
    Bed:      { kind: "Bed",      anchor: "north",    w: 64, h: 38, shape: "rect" },
    Dining:   { kind: "Dining",   anchor: "center",   w: 70, h: 36, shape: "ellipse" },
    Table:    { kind: "Table",    anchor: "center",   w: 48, h: 28, shape: "rect" },
    Island:   { kind: "Island",   anchor: "center",   w: 60, h: 28, shape: "rect" },
    Wardrobe: { kind: "Wardrobe", anchor: "west",     w: 22, h: 60, shape: "rect" },
    Counter:  { kind: "Counter",  anchor: "east",     w: 16, h: 70, shape: "strip" },
    TV:       { kind: "TV",       anchor: "south",    w: 50, h:  8, shape: "strip" },
    Study:    { kind: "Study",    anchor: "north",    w: 50, h: 22, shape: "rect" },
    Bar:      { kind: "Bar",      anchor: "east",     w: 22, h: 44, shape: "rect" },
    Light:    { kind: "Light",    anchor: "overhead", w: 14, h: 14, shape: "circle" },
    Storage:  { kind: "Storage",  anchor: "west",     w: 22, h: 50, shape: "rect" },
    Chair:    { kind: "Chair",    anchor: "center",   w: 16, h: 16, shape: "rect" },
    Rug:      { kind: "Rug",      anchor: "center",   w: 90, h: 56, shape: "rect" },
    Item:     { kind: "Item",     anchor: "center",   w: 36, h: 22, shape: "rect" },
  };
  function classify(sku: GraphNode): CategoryCfg {
    const cat = typeof sku.attrs["category"] === "string" ? (sku.attrs["category"] as string) : "Item";
    return CATEGORY_LAYOUT[cat] ?? CATEGORY_LAYOUT["Item"]!;
  }

  // Track how many of each anchor we've placed so we can stack without overlap.
  const used: Record<Anchor, number> = { center: 0, south: 0, north: 0, east: 0, west: 0, overhead: 0 };
  const pieces: string[] = [];

  function anchorXY(a: Anchor, w: number, h: number, slot: number): { x: number; y: number } {
    const inset = 10;
    switch (a) {
      case "center":   return { x: cx - w / 2,                    y: cy - h / 2 + slot * (h + 6) };
      case "south":    return { x: cx - w / 2 + slot * 6,         y: y0 + rdPx - h - inset - slot * 4 };
      case "north":    return { x: cx - w / 2 + slot * 6,         y: y0 + inset + slot * 4 };
      case "east":     return { x: x0 + rwPx - w - inset,         y: cy - h / 2 + slot * (h + 6) };
      case "west":     return { x: x0 + inset,                    y: cy - h / 2 + slot * (h + 6) };
      case "overhead": return { x: cx - w / 2 - 30 + slot * 24,   y: cy - h / 2 - 8 };
    }
  }

  /** Fallback anchor order — tries the category's preferred wall first, then
   *  rotates through alternatives. center stays last because that's "in the
   *  middle of the room" which often reads oddly for wall-anchored items. */
  const FALLBACK: ReadonlyArray<Anchor> = ["south", "north", "east", "west", "overhead", "center"];

  function safePlace(cfg: CategoryCfg): { anchor: Anchor; x: number; y: number; collided: boolean } {
    const tried: Anchor[] = [cfg.anchor, ...FALLBACK.filter((a) => a !== cfg.anchor)];
    for (const anchor of tried) {
      const slot = used[anchor];
      const { x, y } = anchorXY(anchor, cfg.w, cfg.h, slot);
      const rect: Rect = { x, y, w: cfg.w, h: cfg.h };
      // Out-of-room check first — overhead and rotated anchors might exit the wall.
      const insideRoom =
        x >= x0 - 2 && y >= y0 - 2 &&
        x + cfg.w <= x0 + rwPx + 2 &&
        y + cfg.h <= y0 + rdPx + 2;
      if (!insideRoom) continue;
      const conflicts = doorZones.some((d) => rectsOverlap(rect, d));
      if (conflicts) continue;
      used[anchor] = slot + 1;
      return { anchor, x, y, collided: false };
    }
    // Every anchor conflicts — place at category default and mark collided so the
    // renderer can paint a warning halo. The Why-engine will see this as a real
    // door-clearance violation when B.1 spatial predicates land.
    const slot = used[cfg.anchor]++;
    const { x, y } = anchorXY(cfg.anchor, cfg.w, cfg.h, slot);
    return { anchor: cfg.anchor, x, y, collided: true };
  }

  for (const sku of skus) {
    const cfg = classify(sku);
    const place = safePlace(cfg);
    pieces.push(renderFurnitureIcon(cfg.kind, place.x, place.y, cfg.w, cfg.h, sku.label, place.collided));
  }
  return pieces.join("");
}

/** Architectural-grade furniture icons — modelled after AIA/NCS/IS-962
 *  symbology so a floor plan reads like a real plan, not abstract boxes.
 *  Each icon: outer silhouette + interior detail (cushions / pillow strip /
 *  chair dots / door swing line / hob burners / etc.) + small label. */
function renderFurnitureIcon(kind: string, x: number, y: number, w: number, h: number, fullLabel: string, collided = false): string {
  const FILL = "#FBF5E9";
  const STROKE = "#141612";
  const SOFT = "#94908a";
  const ACCENT = "#C95C1A";
  const detail = (d: string): string => `<g stroke="${SOFT}" stroke-width="0.6" fill="none">${d}</g>`;
  const labelText = (txt: string, cx: number, cy: number, weight = "500", size = 8): string =>
    `<text x="${cx}" y="${cy}" text-anchor="middle" font-family="-apple-system, 'SF Pro Text', Inter, sans-serif" font-size="${size}" font-weight="${weight}" fill="#141612">${escapeXml(txt)}</text>`;

  const cx = x + w / 2;
  const cy = y + h / 2;
  const ariaSuffix = collided ? " (clearance violation: door swing)" : "";
  const aria = `aria-label="${escapeXml(fullLabel + ariaSuffix)}"`;
  // Door-clearance halo — surfaced visually so the designer can SEE the conflict.
  // This is the inline manifestation of the typed clearance rule that B.1 will
  // formalise; today it fires by overlap-with-door-swing, no hardcoding of walls.
  const halo = collided
    ? `<rect x="${x - 3}" y="${y - 3}" width="${w + 6}" height="${h + 6}" rx="5" fill="none" stroke="#C95C1A" stroke-width="1.4" stroke-dasharray="4 3" opacity="0.9"/>`
    : "";

  switch (kind) {
    case "Sofa": {
      // outer pad + back rest line + 3 seat-cushion divisions
      const back = h * 0.32;
      const seatTop = y + back;
      return `<g ${aria}>${halo}
        <rect x="${x}" y="${y}" width="${w}" height="${h}" rx="4" fill="${FILL}" stroke="${STROKE}" stroke-width="0.9"/>
        ${detail(`
          <line x1="${x + 2}" y1="${seatTop}" x2="${x + w - 2}" y2="${seatTop}"/>
          <line x1="${x + w / 3}" y1="${seatTop}" x2="${x + w / 3}" y2="${y + h - 2}"/>
          <line x1="${x + 2 * w / 3}" y1="${seatTop}" x2="${x + 2 * w / 3}" y2="${y + h - 2}"/>
        `)}
        ${labelText("Sofa", cx, cy + 3, "600")}
      </g>`;
    }
    case "Bed": {
      // headboard strip + pillow rectangles + mattress edge line
      const headH = Math.max(4, h * 0.18);
      const pillowH = headH;
      const pillowY = y + headH + 2;
      const pillowW = (w - 8) / 2 - 1;
      return `<g ${aria}>${halo}
        <rect x="${x}" y="${y}" width="${w}" height="${h}" rx="2" fill="${FILL}" stroke="${STROKE}" stroke-width="0.9"/>
        <rect x="${x}" y="${y}" width="${w}" height="${headH}" fill="${SOFT}" opacity="0.18"/>
        ${detail(`<line x1="${x}" y1="${y + headH}" x2="${x + w}" y2="${y + headH}"/>`)}
        <rect x="${x + 4}" y="${pillowY}" width="${pillowW}" height="${pillowH}" rx="1.5" fill="${FILL}" stroke="${SOFT}" stroke-width="0.5"/>
        <rect x="${x + 4 + pillowW + 2}" y="${pillowY}" width="${pillowW}" height="${pillowH}" rx="1.5" fill="${FILL}" stroke="${SOFT}" stroke-width="0.5"/>
        ${labelText("Bed", cx, y + h - 4, "600")}
      </g>`;
    }
    case "Dining": {
      // ellipse table + 6 chair dots around it
      const rx = w / 2, ry = h / 2;
      const chairs = Array.from({ length: 6 }).map((_, i) => {
        const t = (i / 6) * Math.PI * 2;
        const px = cx + Math.cos(t) * (rx + 5);
        const py = cy + Math.sin(t) * (ry + 5);
        return `<circle cx="${px}" cy="${py}" r="2.5" fill="${FILL}" stroke="${STROKE}" stroke-width="0.6"/>`;
      }).join("");
      return `<g ${aria}>${halo}
        <ellipse cx="${cx}" cy="${cy}" rx="${rx}" ry="${ry}" fill="${FILL}" stroke="${STROKE}" stroke-width="0.9"/>
        ${chairs}
        ${labelText("Dining", cx, cy + 3, "600")}
      </g>`;
    }
    case "Table": {
      // rectangle + 4 chair dots
      const chairs = [
        [cx - w / 2 - 4, cy], [cx + w / 2 + 4, cy],
        [cx, cy - h / 2 - 4], [cx, cy + h / 2 + 4],
      ].map(([px, py]) => `<circle cx="${px}" cy="${py}" r="2" fill="${FILL}" stroke="${STROKE}" stroke-width="0.6"/>`).join("");
      return `<g ${aria}>${halo}
        <rect x="${x}" y="${y}" width="${w}" height="${h}" rx="2" fill="${FILL}" stroke="${STROKE}" stroke-width="0.9"/>
        ${chairs}
        ${labelText("Table", cx, cy + 3)}
      </g>`;
    }
    case "Island": {
      // kitchen island: rect + 4 hob burner circles
      const burnerPositions: ReadonlyArray<[number, number]> = [
        [0.28, 0.32], [0.28, 0.68], [0.72, 0.32], [0.72, 0.68],
      ];
      const burners = burnerPositions.map(([tx, ty]) =>
        `<circle cx="${x + w * tx}" cy="${y + h * ty}" r="2.5" fill="none" stroke="${ACCENT}" stroke-width="0.8"/>`
      ).join("");
      return `<g ${aria}>${halo}
        <rect x="${x}" y="${y}" width="${w}" height="${h}" rx="2" fill="${FILL}" stroke="${STROKE}" stroke-width="0.9"/>
        ${burners}
        ${labelText("Island", cx, cy + 3, "600")}
      </g>`;
    }
    case "Wardrobe": {
      // tall rect + 2 vertical doors with hinge dots
      const door = w / 2;
      return `<g ${aria}>${halo}
        <rect x="${x}" y="${y}" width="${w}" height="${h}" fill="${FILL}" stroke="${STROKE}" stroke-width="0.9"/>
        ${detail(`<line x1="${x + door}" y1="${y + 2}" x2="${x + door}" y2="${y + h - 2}"/>`)}
        <circle cx="${x + door - 2}" cy="${y + h / 2}" r="0.8" fill="${STROKE}"/>
        <circle cx="${x + door + 2}" cy="${y + h / 2}" r="0.8" fill="${STROKE}"/>
        ${labelText("Wardrobe", cx, cy + 3, "500", 7.5)}
      </g>`;
    }
    case "Counter": {
      // narrow strip with sink hole
      return `<g ${aria}>${halo}
        <rect x="${x}" y="${y}" width="${w}" height="${h}" rx="1.5" fill="${FILL}" stroke="${STROKE}" stroke-width="0.9"/>
        <rect x="${x + 2}" y="${y + h * 0.4}" width="${w - 4}" height="${Math.max(4, h * 0.2)}" rx="1" fill="none" stroke="${SOFT}" stroke-width="0.6"/>
        ${labelText("Counter", cx, y - 3, "500", 7)}
      </g>`;
    }
    case "TV": {
      // wall strip with screen bevel
      return `<g ${aria}>${halo}
        <rect x="${x}" y="${y}" width="${w}" height="${h}" rx="0.5" fill="${STROKE}" stroke="${STROKE}" stroke-width="0.6"/>
        ${labelText("TV", cx, y - 3, "500", 7)}
      </g>`;
    }
    case "Study": {
      return `<g ${aria}>${halo}
        <rect x="${x}" y="${y}" width="${w}" height="${h}" rx="2" fill="${FILL}" stroke="${STROKE}" stroke-width="0.9"/>
        ${detail(`<line x1="${x + 4}" y1="${y + h * 0.6}" x2="${x + w - 4}" y2="${y + h * 0.6}"/>`)}
        <circle cx="${cx}" cy="${y + h - 3}" r="1.8" fill="${FILL}" stroke="${STROKE}" stroke-width="0.6"/>
        ${labelText("Study", cx, cy, "600")}
      </g>`;
    }
    case "Bar": {
      return `<g ${aria}>${halo}
        <rect x="${x}" y="${y}" width="${w}" height="${h}" rx="2" fill="${FILL}" stroke="${STROKE}" stroke-width="0.9"/>
        ${detail(`<line x1="${x + 2}" y1="${y + h / 2}" x2="${x + w - 2}" y2="${y + h / 2}"/>`)}
        ${labelText("Bar", cx, cy + 3, "600")}
      </g>`;
    }
    case "Light": {
      // pendant: outer circle + 4 cross arms + central bulb dot
      const r = w / 2;
      return `<g ${aria}>
        <circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="${STROKE}" stroke-width="0.9"/>
        <line x1="${cx - r}" y1="${cy}" x2="${cx + r}" y2="${cy}" stroke="${STROKE}" stroke-width="0.6"/>
        <line x1="${cx}" y1="${cy - r}" x2="${cx}" y2="${cy + r}" stroke="${STROKE}" stroke-width="0.6"/>
        <circle cx="${cx}" cy="${cy}" r="${r / 3}" fill="${ACCENT}"/>
      </g>`;
    }
    case "Item":
    default:
      return `<g ${aria}>${halo}
        <rect x="${x}" y="${y}" width="${w}" height="${h}" rx="2" fill="${FILL}" stroke="${STROKE}" stroke-width="0.9"/>
        ${labelText("Item", cx, cy + 3)}
      </g>`;
  }
}

function escapeXml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}
